import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function KinRadiusMap({
  initialCenter = [-37.8136, 144.9631],
  initialRadius = 30,
  onChange,
  readOnly = false,
  className = ''
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const circleRef = useRef(null);
  const markerRef = useRef(null);

  const [center, setCenter] = useState(initialCenter);
  const [radius, setRadius] = useState(initialRadius);
  const [address, setAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const [lat, lng] = center;

    // Create map - using same tile server as DeliveryMap
    const map = L.map(mapRef.current, {
      center: [lat, lng],
      zoom: 12,
      zoomControl: true,
    });

    // Use CartoDB tile server (same as DeliveryMap - avoids OSM referer issues)
L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CartoDB',
  subdomains: 'abcd',
  maxZoom: 19
}).addTo(map);

    // Add marker at center
    const marker = L.marker([lat, lng], {
      draggable: !readOnly,
    }).addTo(map);
    marker.bindPopup('📍 Kin Centre');
    markerRef.current = marker;

    // Add circle for radius
    const circle = L.circle([lat, lng], {
      radius: radius * 1000,
      color: '#16a34a',
      fillColor: '#16a34a',
      fillOpacity: 0.15,
      weight: 3,
      dashArray: '5, 5',
    }).addTo(map);
    circleRef.current = circle;

    // Add radius label
    const label = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'radius-label',
        html: `<div style="background: rgba(22, 163, 74, 0.9); color: white; padding: 2px 10px; border-radius: 12px; font-size: 13px; font-weight: bold;">${radius} km radius</div>`,
        iconSize: [100, 24],
        iconAnchor: [50, -20],
      })
    }).addTo(map);

    // Handle marker drag
    marker.on('dragend', async (e) => {
      const position = e.target.getLatLng();
      const newLat = position.lat;
      const newLng = position.lng;
      
      setCenter([newLat, newLng]);
      
      if (circleRef.current) {
        circleRef.current.setLatLng([newLat, newLng]);
      }
      label.setLatLng([newLat, newLng]);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/geocode/reverse?lat=${newLat}&lon=${newLng}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.address) setAddress(data.address);
        }
      } catch (err) {
        console.error('Reverse geocode error:', err);
      }
    });

    // Handle click on map
    map.on('click', async (e) => {
      if (readOnly) return;
      const { lat, lng } = e.latlng;
      
      setCenter([lat, lng]);
      
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      }
      if (circleRef.current) {
        circleRef.current.setLatLng([lat, lng]);
      }
      label.setLatLng([lat, lng]);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/geocode/reverse?lat=${lat}&lon=${lng}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.address) setAddress(data.address);
        }
      } catch (err) {
        console.error('Reverse geocode error:', err);
      }
    });

    // Fit map to show the circle
    setTimeout(() => {
      if (circleRef.current) {
        const bounds = circleRef.current.getBounds();
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }, 100);

    mapInstanceRef.current = map;

    // Store update function for radius changes
    window.updateKinRadiusMap = (newRadius) => {
      if (circleRef.current) {
        circleRef.current.setRadius(newRadius * 1000);
        const centerPos = circleRef.current.getLatLng();
        label.setLatLng(centerPos);
        label.setIcon(L.divIcon({
          className: 'radius-label',
          html: `<div style="background: rgba(22, 163, 74, 0.9); color: white; padding: 2px 10px; border-radius: 12px; font-size: 13px; font-weight: bold;">${newRadius} km radius</div>`,
          iconSize: [100, 24],
          iconAnchor: [50, -20],
        }));
      }
    };

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [center[0], center[1], readOnly]);

  // Update radius when slider changes
  useEffect(() => {
    if (window.updateKinRadiusMap) {
      window.updateKinRadiusMap(radius);
    }
    if (circleRef.current) {
      circleRef.current.setRadius(radius * 1000);
    }
  }, [radius]);

  // Notify parent when center or radius changes
  useEffect(() => {
    if (onChange) {
      onChange({
        center: { lat: center[0], lng: center[1] },
        radius: radius,
        address: address
      });
    }
  }, [center, radius, address]);

  // Geocode address
  const geocodeAddress = async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/geocode?q=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          setSearchResults(data);
          setShowSearch(true);
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddressSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    setCenter([lat, lng]);
    setAddress(result.display_name);
    setSearchResults([]);
    setShowSearch(false);
    
    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([lat, lng], 13);
    }
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
    if (circleRef.current) {
      circleRef.current.setLatLng([lat, lng]);
    }
  };

  const area = Math.round(Math.PI * radius * radius);

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <h3 className="text-xl font-semibold mb-2 text-gray-800">📍 Set Your Kin's Service Area</h3>
      <p className="text-sm text-gray-600 mb-4">
        Drag the marker or click the map to set your Kin's centre.
        Use the slider below to adjust the radius.
      </p>

      {/* Address Search */}
      <div className="mb-4 relative">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search for a suburb or address..."
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && geocodeAddress(address)}
            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            disabled={readOnly}
          />
          <button
            onClick={() => geocodeAddress(address)}
            disabled={isSearching || readOnly}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition text-sm"
          >
            {isSearching ? '🔍' : 'Find'}
          </button>
        </div>
        
        {showSearch && searchResults.length > 0 && !readOnly && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {searchResults.map((result, index) => (
              <button
                key={index}
                onClick={() => handleAddressSelect(result)}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b last:border-b-0 text-sm"
              >
                <div className="font-medium">{result.display_name}</div>
                <div className="text-xs text-gray-500">{result.type || 'Location'}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map Container */}
      <div 
        ref={mapRef} 
        style={{ height: '400px', width: '100%' }} 
        className="rounded-lg overflow-hidden border border-gray-200"
      />

      {/* Radius Slider */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-1">
          <label className="font-medium text-gray-700 text-sm">Service Radius</label>
          <span className="text-lg font-bold text-green-600">{radius} km</span>
        </div>
        <input
          type="range"
          min="5"
          max="100"
          step="5"
          value={radius}
          onChange={(e) => setRadius(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
          disabled={readOnly}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>5 km</span>
          <span className="text-center">
            {radius <= 15 ? '🚶 Hyperlocal' : radius <= 40 ? '🚗 Local' : '🚚 Regional'}
          </span>
          <span>100 km</span>
        </div>
      </div>

      {/* Info Cards */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="p-2 bg-blue-50 rounded-lg text-center">
          <div className="text-xs text-blue-700 font-medium">📍 Centre</div>
          <div className="text-xs text-blue-600 truncate">
            {address ? address.split(',').slice(0, 2).join(',') : `${center[0].toFixed(4)}, ${center[1].toFixed(4)}`}
          </div>
        </div>
        <div className="p-2 bg-green-50 rounded-lg text-center">
          <div className="text-xs text-green-700 font-medium">📐 Radius</div>
          <div className="text-xs text-green-600">{radius} km</div>
        </div>
        <div className="p-2 bg-purple-50 rounded-lg text-center">
          <div className="text-xs text-purple-700 font-medium">🗺️ Coverage</div>
          <div className="text-xs text-purple-600">{area.toLocaleString()} km²</div>
        </div>
      </div>

      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-xs text-amber-800">
          💡 <strong>Tip:</strong> 5-15km for city, 20-30km for regional, 50km+ for rural areas.
          Drag the marker or click the map to set your centre.
        </p>
      </div>
    </div>
  );
}
